import { computed, ref, watch, type Ref, type WatchSource } from 'vue';

export type LazyLoadedRowsPageChangeEvent = {
  first: number;
};

export type UseLazyLoadedRowsPaginationOptions<TItem> = {
  rows: Ref<readonly TItem[]>;
  pageSize: number;
  hasMore: Ref<boolean>;
  loadMore: () => Promise<void> | void;
  resetSources?: WatchSource[];
};

export const useLazyLoadedRowsPagination = <TItem>({
  rows,
  pageSize,
  hasMore,
  loadMore,
  resetSources = [],
}: UseLazyLoadedRowsPaginationOptions<TItem>) => {
  const currentPageStart = ref(0);
  const totalLoadedRecords = computed(() => rows.value.length);
  const visibleRows = computed(() => {
    return rows.value.slice(currentPageStart.value, currentPageStart.value + pageSize);
  });
  const displayTotalRecords = computed(() => {
    if (!hasMore.value) return totalLoadedRecords.value;

    return Math.max(totalLoadedRecords.value, currentPageStart.value + pageSize) + 1;
  });

  const getLoadedPageFirst = (requestedFirst: number) => {
    if (totalLoadedRecords.value === 0) return 0;

    const lastLoadedPageFirst = Math.floor((totalLoadedRecords.value - 1) / pageSize) * pageSize;
    return Math.min(requestedFirst, lastLoadedPageFirst);
  };

  const resetPageStart = () => {
    currentPageStart.value = 0;
  };

  const handlePageChange = async (event: LazyLoadedRowsPageChangeEvent) => {
    const requestedFirst = event.first;
    currentPageStart.value = requestedFirst;

    if (requestedFirst >= totalLoadedRecords.value && hasMore.value) {
      try {
        await loadMore();
      } catch {}

      currentPageStart.value = getLoadedPageFirst(requestedFirst);
      return;
    }

    currentPageStart.value = getLoadedPageFirst(requestedFirst);
  };

  if (resetSources.length > 0) {
    watch(resetSources, resetPageStart);
  }

  return {
    currentPageStart,
    visibleRows,
    displayTotalRecords,
    handlePageChange,
    resetPageStart,
  };
};
